import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, MessageCircleIcon } from 'lucide-react';
import { founder, salon } from '../../data/content';
import { shopLabels } from '../../data/shopLabels';
import { btnSecondary } from '../../ui/styles';
import { closingHeading } from '../../ui/sections';
import { MediaView } from '../shared/MediaView';
import { useCatalog } from '../../contexts/CatalogContext';
import { NotFound } from '../layout/NotFound';
import { ArticleBlock } from './ArticleBlock';
import { MentionedProducts } from './MentionedProducts';
import { articlePath, relatedArticles } from './journalRules';

/** Eén journalartikel: kop, tekst, genoemde producten ernaast en "Lees ook". */
export function Article() {
  const {
    slug = ''
  } = useParams();
  const {
    articles,
    articleBySlug,
    productsByIds
  } = useCatalog();
  const article = articleBySlug(slug);
  const navigate = useNavigate();
  // Uit het journal gekomen (met of zonder categorie): één stap terug, met dezelfde keuze en scrollplek.
  const {
    state
  } = useLocation() as {
    state: {
      fromJournal?: string;
    } | null;
  };
  const fromJournal = state?.fromJournal?.startsWith('/journal') ? state.fromJournal : null;
  if (!article) {
    return <NotFound title="Dit artikel bestaat niet (meer)" body="Misschien heeft het een nieuwe titel gekregen. Alle artikelen staan in het journal." backTo={{
      to: '/journal',
      label: 'Naar alle artikelen'
    }} data-id="element-7548" data-mp="2q8nsz4ob5vih" />;
  }
  const mentioned = productsByIds(article.productIds ?? []);
  const related = relatedArticles(articles, article);
  return <>
      <article className="bg-sand" data-id="element-7549" data-mp="26j3ou7j61bgs">
        <header className="mx-auto max-w-content px-6 pb-10 pt-10 lg:px-10 lg:pt-14" data-id="element-7550" data-mp="26i2c7ymi4gwy">
          <Link to={fromJournal ?? '/journal'} onClick={e => {
          if (!fromJournal) return;
          e.preventDefault();
          navigate(-1);
        }} className="-ml-1 inline-flex min-h-[44px] items-center gap-2 px-1 text-[14px] text-muted transition-colors duration-150 ease-soft hover:text-ink" data-id="element-7551" data-mp="2bcq2o312fle6">
            <ArrowLeftIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7552" data-mp="2h5c8qzdq6mdz" />
            <span data-id="element-7553" data-mp="2vtzygp0w41w0">
              <span className="sr-only" data-id="element-7554" data-mp="2vui6eiwx04jr">Terug naar het </span>Journal
            </span>
          </Link>
          <p className="mt-10 text-[14px] text-clay" data-id="element-7555" data-mp="2qwgjfmwdbl30">
            {article.category} · {article.readTime} lezen · {article.date}
          </p>
          <h1 className="mt-4 max-w-3xl font-display text-[2.5rem] font-light leading-[1.04] text-ink sm:text-[3.6rem]" data-id="element-7556" data-mp="1y7j3hqfmq0df">{article.title}</h1>
          <p className="mt-6 max-w-2xl text-[18px] leading-relaxed text-muted" data-id="element-7557" data-mp="2c7txh2c8jv2t">{article.excerpt}</p>
          <div className="mt-8 flex items-center gap-3" data-id="element-7558" data-mp="284nuq4oc0m13">
            <img src={founder.portrait} alt="" width={44} height={44} className="h-11 w-11 shrink-0 rounded-full object-cover object-top" data-id="element-7559" data-mp="3nlxobznj133n" />
            <p className="text-[14px] leading-snug text-muted" data-id="element-7560" data-mp="3jjucvb0uxyid">
              <span className="block text-ink" data-id="element-7561" data-mp="3r3lqan29pfpw">
                <span className="sr-only" data-id="element-7562" data-mp="3r2ttj8wxpby1">Geschreven door </span>
                {founder.name}
              </span>
              {founder.role}
            </p>
          </div>
        </header>

        {article.image && <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7563" data-mp="3h8bmpuw810a0">
            {/* Sfeerbeeld: de titel staat er al boven. */}
            <MediaView media={{
          image: article.image,
          alt: ''
        }} priority sizes="(min-width: 1280px) 1200px, 100vw" className="aspect-[4/3] w-full sm:aspect-[16/8]" data-id="element-7564" data-mp="dpi9xkb9ofgw" />
          </div>}

        <div className="mx-auto grid max-w-content gap-14 px-6 pb-24 pt-12 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-7565" data-mp="3zp1gs6f8ggv">
          <div className="max-w-[680px] lg:col-span-7" data-id="element-7566" data-mp="8y09qyh2iiwz">
            {article.body.map((block, i) =>
          // Blokken hebben (nog) geen eigen id; de volgorde is vast zolang je leest.
          <ArticleBlock key={i} block={block} data-id="element-7567" data-mp="11fcpwaqvuk5d" data-mpi={i} />)}

            <div className="mt-14 flex flex-col gap-4 border-t border-line pt-8 sm:flex-row sm:items-center sm:justify-between" data-id="element-7568" data-mp="11gnsi78ldluw">
              <p className="text-[15px] leading-relaxed text-muted" data-id="element-7569" data-mp="34w9jfgqi0ttz">Twijfel je of dit voor jouw huid geldt? App me gerust, ik antwoord zelf.</p>
              <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`${btnSecondary} shrink-0`} data-id="element-7570" data-mp="2zy7yeq9iikfv">
                <MessageCircleIcon className="h-4 w-4 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7571" data-mp="2v0pp2yrl237c" />
                App {founder.firstName}
                <span className="sr-only" data-id="element-7572" data-mp="3nmqyiiierqwv">{shopLabels.viaWhatsApp}</span>
              </a>
            </div>
          </div>

          {mentioned.length > 0 && <aside className="lg:col-span-4 lg:col-start-9" aria-labelledby="genoemd" data-id="element-7573" data-mp="3iuhe1i02bj1z">
              <div className="bg-shell p-6 lg:sticky lg:top-32" data-id="element-7574" data-mp="3dx7evamvft0y">
                <h2 id="genoemd" className="font-display text-xl text-ink" data-id="element-7575" data-mp="f8iliisdd8pt">
                  Genoemd in dit artikel
                </h2>
                <div className="mt-1" data-id="element-7576" data-mp="aentms2m1nfn">
                  <MentionedProducts products={mentioned} variant="sidebar" data-id="element-7577" data-mp="ipwj2dlbjbmu" />
                </div>
              </div>
            </aside>}
        </div>
      </article>

      {related.length > 0 && <section className="border-t border-line bg-shell py-16 lg:py-20" aria-labelledby="lees-ook" data-id="element-7578" data-mp="rptd6uqh92pa">
          <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7579" data-mp="wpfpn1yheex1">
            <h2 id="lees-ook" className={`${closingHeading} text-ink`} data-id="element-7580" data-mp="11o8ts3rvotkj">
              Lees ook
            </h2>
            <ul className="mt-8 grid gap-8 md:grid-cols-2" data-id="element-7581" data-mp="15plyubn5csqe">
              {related.map(a => <li key={a.slug} data-id="element-7582" data-mp="1agu8n8se2g43">
                  <Link to={articlePath(a)} className="group block border-t border-ink/15 pt-6" data-id="element-7583" data-mp="1ahvm1jwvneoz">
                    <p className="text-[13px] text-clay" data-id="element-7584" data-mp="1fecc4jnn034p">
                      {a.category} · {a.readTime}
                    </p>
                    <h3 className="mt-2 font-display text-2xl font-light leading-snug text-ink transition-colors duration-150 ease-soft group-hover:text-clay" data-id="element-7585" data-mp="1kcnn4xpo4joi">
                      {a.title}
                    </h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-muted" data-id="element-7586" data-mp="uqmkv8rbjeno">{a.excerpt}</p>
                    <span className="mt-4 inline-flex items-center gap-2 text-sm text-ink" aria-hidden="true" data-id="element-7587" data-mp="uorzjtc4xkj2">
                      Lees verder
                      <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} data-id="element-7588" data-mp="14nr5v59fh42w" />
                    </span>
                  </Link>
                </li>)}
            </ul>
          </div>
        </section>}
    </>;
}